import RegularText from '@/components/RegularText';
import { config } from '@/config';
import { useGetShopThemesQuery } from '@/redux/query';
import { Badge, Box, Button, Card, InlineStack, Link, Select, SkeletonBodyText } from '@shopify/polaris';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const EmbedAppStatus = () => {
  const { t } = useTranslation('widget_settings');
  const { data: shopThemesData, isLoading: isLoadingShopThemes } = useGetShopThemesQuery({ shop: config.shop });
  const [theme, setTheme] = useState(shopThemesData?.find((item) => item.role === 'MAIN')?.id || '');
  const appEmbedId = process.env.REACT_APP_SHOPIFY_THEME_APP_EXTENSION_ID;

  const themeOptions = useMemo(
    () =>
      shopThemesData?.map((item) => ({
        value: item.id,
        label: `${item.name} ${item.role === 'MAIN' ? '(Live theme)' : ''}`,
      })),
    [shopThemesData],
  );

  const handleChangeTheme = useCallback((value: string) => setTheme(value), []);

  const isActiveStatus = useMemo(() => {
    const selectedTheme = shopThemesData?.find((item) => item.id === theme);
    const settingsFile = selectedTheme?.files.find((f) => f.filename === 'config/settings_data.json');
    const blocks = settingsFile?.file?.current?.blocks;

    if (blocks && selectedTheme && appEmbedId) {
      return Object.values(blocks).some((block: any) => block.type.includes(appEmbedId) && !block.disabled);
    }

    return false;
  }, [appEmbedId, shopThemesData, theme]);

  const handleOnClickPreview = (id: string) => {
    const themeId = id.split('/')[id.split('/').length - 1];
    window.open(
      `https://${config.shop}/admin/themes/${themeId}/editor?context=apps&template=cart&activateAppId=${appEmbedId}/app-embed-block.`,
      '_blank',
    );
  };

  useEffect(() => {
    if (shopThemesData) {
      setTheme(shopThemesData.find((item) => item.role === 'MAIN')?.id || '');
    }
  }, [shopThemesData]);

  if (isLoadingShopThemes) {
    return (
      <Card padding={'300'}>
        <SkeletonBodyText lines={7} />
      </Card>
    );
  }

  return (
    <Card padding={'300'}>
      <InlineStack gap={'600'}>
        <RegularText>{t('position.embed.title')}</RegularText>
        <Badge tone={isActiveStatus ? 'success' : undefined}>
          {isActiveStatus ? t('position.embed.active') : t('position.embed.deactive')}
        </Badge>
      </InlineStack>
      <Box paddingBlockStart={'200'} paddingBlockEnd={!isActiveStatus ? '300' : '0'}>
        <Select
          helpText={t('position.embed.helpText')}
          label=""
          labelHidden
          options={themeOptions}
          value={theme}
          onChange={handleChangeTheme}
        />
      </Box>
      {!isActiveStatus && (
        <InlineStack gap={'400'}>
          <Button variant="primary" onClick={() => handleOnClickPreview(theme)}>
            {t('position.embed.button')}
          </Button>
          <Link onClick={() => window.open('https://docs.dingdoong.io/dashboard/dashboard#embed-app')} removeUnderline>
            {t('position.embed.instruction')}
          </Link>
        </InlineStack>
      )}
    </Card>
  );
};
